import { Box, Stack, Typography } from "@mui/material";
import type {
  RejectionDocumentDto,
  RejectionGroupDto,
} from "Infrastructure/Api/Api";
import { Resources, type TFunction, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.OrbitL.Rejection;

// The processor's own document codes. Anything outside this list is shown as it arrived rather than
// swallowed — a new code must not hide the rejection it belongs to.
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  ID_CARD: RESOURCES.DocumentTypes.IdCard,
  PASSPORT: RESOURCES.DocumentTypes.Passport,
  DRIVER_LICENSE: RESOURCES.DocumentTypes.DriverLicense,
  OTHER_DOCUMENT: RESOURCES.DocumentTypes.OtherDocument,
};

const getDocumentLabel = (
  { documentType, documentNumber }: RejectionDocumentDto,
  t: TFunction,
) => {
  const typeResource = documentType
    ? DOCUMENT_TYPE_LABELS[documentType]
    : undefined;
  const typeLabel = typeResource ? t(typeResource) : documentType;

  if (!typeLabel) {
    return null;
  }

  return documentNumber
    ? t(RESOURCES.DocumentLabel, { type: typeLabel, number: documentNumber })
    : typeLabel;
};

type RejectionDocumentProps = {
  document: RejectionDocumentDto;
};

const RejectionDocument = ({ document }: RejectionDocumentProps) => {
  const { t } = useResource();
  const documentLabel = getDocumentLabel(document, t);

  return (
    <Box>
      {documentLabel && (
        <Typography variant="body2" color="text.secondary">
          {documentLabel}
        </Typography>
      )}
      <Box component="ul" m={0} pl={2.5}>
        {document.messages.map((message) => (
          <Typography component="li" variant="body2" key={message}>
            {message}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

type Props = {
  groups: Array<RejectionGroupDto>;
};

/**
 * Rejections grouped by the person and document they belong to. The messages are the processor's
 * wording, verbatim — support quotes them back, so they are never rephrased or translated.
 */
export const RejectionGroups = ({ groups }: Props) => {
  const { t } = useResource();

  return (
    <Stack gap={2}>
      {groups.map(({ partyPublicID, displayName, documents }) => (
        <Stack key={partyPublicID ?? "contract"} gap={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {displayName ?? t(RESOURCES.ContractGroup)}
          </Typography>
          {documents.map((document, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no unique identity (type and number are both optional) and the list is a render-only snapshot
            <RejectionDocument key={index} document={document} />
          ))}
        </Stack>
      ))}
    </Stack>
  );
};
